/// <reference types="vite/client" />
import {
	createRootRouteWithContext,
	HeadContent,
	Scripts,
	useMatch,
	useRouter,
} from '@tanstack/react-router';
import type * as React from 'react';
import { useEffect } from 'react';
import { DefaultCatchBoundary } from '@/components/DefaultCatchBoundary';
import LinearProgress from '@/components/linear-progress';
import { NotFound } from '@/components/NotFound';
import { LoginProvider, logoutPwa } from '@/lib/login';
import { getAuth, getBrowserAuth } from '@/lib/login/auth';
import { isLoginStatusChange, isSignOut } from '@/lib/login/auth-change';
import { onReady } from '@/lib/pwa';
import appCss from '@/styles/app.css?url';
import { ClientThemeStyle } from '@/utils/app-context/client-theme-style';
import type { AppContext } from '@/utils/headers';
import { seo } from '@/utils/seo';
import { ThemeColorSync } from '@/utils/theme';

export const Route = createRootRouteWithContext<AppContext>()({
	head: () => ({
		meta: [
			{
				charSet: 'utf-8',
			},
			{
				name: 'viewport',
				content: 'width=device-width, initial-scale=1',
			},
			...seo({
				title: 'Ixigo Finance',
				description:
					'Manage payments, credit cards, and financial services with Ixigo Finance',
			}),
			{
				name: 'robots',
				content: 'noindex, nofollow',
			},
		],
		links: [
			{ rel: 'stylesheet', href: appCss },
			{
				rel: 'icon',
				href: 'https://images.ixigo.com/image/upload/new/3c5218eaccbc128a6c0ec461a627daf8-mbsmf.png',
			},
		],
		scripts: [],
	}),
	errorComponent: (props) => {
		return (
			<RootDocument isError>
				<DefaultCatchBoundary {...props} />
			</RootDocument>
		);
	},
	notFoundComponent: () => <NotFound />,
	shellComponent: RootComponent,
	ssr: true,
	loader: async ({ context }) => {
		// Request-scoped server auth on the server, browser singleton on the client.
		// Seed the serializable state so the first client render matches SSR.
		const auth = await getAuth();
		return {
			...context._theme,
			initialAuthState: auth.getState(),
		};
	},
});

function RootComponent({ children }: { children: React.ReactNode }) {
	const router = useRouter();
	// React to auth changes from the SDK store. Two distinct signals:
	//  - isSignOut: a real authenticated → anonymous transition (e.g. a 401 the
	//    interceptor handled) → notify the native shell. Not a plain anonymous
	//    load or a resolving → anonymous settle.
	//  - isLoginStatusChange: the login STATUS flipped → refresh route data.
	// We deliberately do NOT invalidate on the lazy user-object hydration event
	// (`authenticated, user:null → user:obj`, status unchanged): loaders read only
	// isLoggedIn(), so invalidating there re-runs every loader and remounts the
	// page, double-firing data calls (eligibility / common-logging / the MPP
	// loader) and flickering the UI. Replaces the old reload-on-401.
	useEffect(
		() =>
			getBrowserAuth().subscribe((event) => {
				if (isSignOut(event)) {
					logoutPwa();
				}
				if (isLoginStatusChange(event)) {
					router.invalidate();
				}
			}),
		[router],
	);
	return <RootDocument>{children}</RootDocument>;
}

function RootDocument({
	children,
	isError = false,
}: {
	children: React.ReactNode;
	isError?: boolean;
}) {
	// CBCC streams its lazy experience after the shell. Its layout owns the
	// native ready signal so the host loader covers that initial render.
	const deferPwaReady = useMatch({
		from: '/travel-credit-card',
		shouldThrow: false,
		select: () => true,
	});
	const {
		style: themeCss,
		script: themeScript,
		initialAuthState,
	} = Route.useLoaderData() || {};
	return (
		<html lang="en" suppressHydrationWarning>
			<head>
				<ThemeColorSync />
				{themeScript ? (
					<script
						// biome-ignore lint/security/noDangerouslySetInnerHtml: internal inline script for theme
						dangerouslySetInnerHTML={{ __html: themeScript }}
					/>
				) : null}
				{!deferPwaReady || isError ? (
					<script
						// biome-ignore lint/security/noDangerouslySetInnerHtml: internal script to handle pwa ready
						dangerouslySetInnerHTML={{ __html: `(${onReady.toString()})();` }}
					/>
				) : null}
				<HeadContent />
				<ClientThemeStyle initialCss={themeCss} />
			</head>
			<body className="bg-primary text-primary font-sans">
				<LoginProvider initialAuthState={initialAuthState}>
					{children}
				</LoginProvider>
				<LinearProgress />
				<Scripts />
			</body>
		</html>
	);
}
